// Merge several uploaded photos into ONE composite image so a multi-image template
// sends a single reference to the AI. Uses a TIGHT 2-column masonry: each photo keeps
// its own aspect ratio, fills the full column width (no side bars, never cropped), and
// is placed into the currently-shortest column — so there are no empty cells and almost
// no wasted white space. This matters because the AI squeezes the whole merged image
// into a small box, so wasted white = wasted face detail. Returns a JPEG File.
export async function mergeImagesToGrid(files) {
    const loadImg = (file) => new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
        img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
        img.src = url;
    });
    const imgs = await Promise.all(files.map(loadImg));
    const cols = imgs.length <= 1 ? 1 : 2;
    const GAP = 10;
    const COLW = 560;                       // content width per column (px)
    const totalW = cols * COLW + (cols + 1) * GAP;

    // Masonry placement: each image goes to the shortest column at full column width,
    // its height derived from the real aspect ratio (so nothing is stretched/cropped).
    const colBottoms = new Array(cols).fill(GAP);
    const placements = imgs.map((img) => {
        const h = Math.max(1, Math.round(COLW * (img.naturalHeight / img.naturalWidth)));
        let ci = 0;
        for (let i = 1; i < cols; i++) if (colBottoms[i] < colBottoms[ci]) ci = i;
        const x = GAP + ci * (COLW + GAP);
        const y = colBottoms[ci];
        colBottoms[ci] += h + GAP;
        return { img, x, y, w: COLW, h };
    });
    const totalH = Math.max(...colBottoms);

    const canvas = document.createElement('canvas');
    canvas.width = totalW; canvas.height = totalH;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, totalW, totalH);
    ctx.imageSmoothingQuality = 'high';
    placements.forEach(p => ctx.drawImage(p.img, p.x, p.y, p.w, p.h));

    const blob = await new Promise((res, rej) => canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/jpeg', 0.92));
    return new File([blob], 'merged.jpg', { type: 'image/jpeg' });
}
